import booleanPointOnLine from '@turf/boolean-point-on-line';
import { type LineString, lineString } from '@turf/helpers';
import turfLength from '@turf/length';
import turfNearestPointOnLine from '@turf/nearest-point-on-line';
import isEqual from 'lodash.isequal';
import { Costings } from '@alltrails/types/CustomRoute';
import { ServerRoute } from '../../redux/customRoutesApi';
import type { GroupedSegment } from '../../types/CustomRoute';
import type { LatLng, LngLat } from '../../types/Geo';

type RouteBetweenPointsOptions = {
  ignoreOrigStart?: boolean;
  ignoreOrigEnd?: boolean;
};

export type RouteAlongLineStringResult = {
  data?: {
    costings: Costings;
    groupedSegments: GroupedSegment[];
  };
  isSuccess: boolean;
};

const pointsEqual = (pt1: LngLat, pt2: LngLat): boolean =>
  isEqual(
    pt1.map(n => n.toFixed(6)),
    pt2.map(n => n.toFixed(6))
  );

const getPath = (vertices: LngLat[], startIndex: number, endIndex: number): LngLat[] => {
  // start at next point after start index
  const startToLastVertice = vertices.reduce<LngLat[]>((acc, vertex, i) => {
    // If we cross over the end, route all the way to the end and capture the rest with the reduce below
    if (i > startIndex && (i <= endIndex || endIndex < startIndex)) {
      acc.push(vertex);
    }
    return acc;
  }, []);

  const firstVerticeToEnd = vertices.reduce<LngLat[]>((acc, vertex, i) => {
    if (endIndex < startIndex && i <= endIndex) {
      acc.push(vertex);
    }
    return acc;
  }, []);

  return startToLastVertice.concat(firstVerticeToEnd);
};

const removeDuplicateVertices = (vertices: LngLat[]): LngLat[] => {
  const cleaned = vertices.reduce<LngLat[]>((acc, vertex, i) => {
    if (i === 0 || !isEqual(acc[acc.length - 1], vertex)) {
      acc.push(vertex);
    }
    return acc;
  }, []);
  return cleaned;
};

const oppositeIndex = (index: number, array: LngLat[]): number => array.length - 1 - index;

const pointHasManyMatches = (vertices: LngLat[], index: number): boolean => {
  const point = vertices[index];
  return vertices.filter(vertice => pointsEqual(vertice, point)).length > 1;
};

const findTopLinePointIndex = (vertices: LngLat[], index: number): number => {
  const point = vertices[index];
  const first = vertices.findIndex(vertice => pointsEqual(vertice, point));
  return first !== index ? first - 1 : index;
};

const findClosestPointIndex = (vertices: LngLat[], startIndex: number, targetIndex: number): number => {
  const targetPoint = vertices[targetIndex];
  const allIndexesOfTarget = vertices.reduce<number[]>(
    (indexes, vertice, i) => (pointsEqual(vertice, targetPoint) ? indexes.concat(i) : indexes),
    []
  );

  if (allIndexesOfTarget.length === 1) {
    return targetIndex;
  }

  const shortestIndexWithLength = allIndexesOfTarget.reduce<[number, number]>(
    (shortest, index) => {
      // get length from start to target
      const path = getPath(vertices, startIndex, index);
      const route = lineString(path);
      const length = turfLength(route);
      if (length < shortest[1]) {
        return [index, length];
      }
      return shortest;
    },
    [0, Infinity]
  );
  return shortestIndexWithLength[0];
};

const routeBetweenPoints = (
  pts: LngLat[],
  lineStringObj: LineString,
  { ignoreOrigStart = false, ignoreOrigEnd = false }: RouteBetweenPointsOptions = {}
): LngLat[] => {
  const fromPoint = pts[0];
  const placedPoint = pts[pts.length - 1];
  const vertices = lineStringObj.coordinates as LngLat[];
  const lineSuggestion = lineString(vertices);
  // this epsilon value was found through trial and error, but in testing will always return the correct boolean result
  const isStartPointOnLine = booleanPointOnLine(fromPoint, lineSuggestion, { epsilon: 5e-10 });

  const nearestToStartPointResult = turfNearestPointOnLine(lineSuggestion, fromPoint);
  const nearestToPlacedPointResult = turfNearestPointOnLine(lineSuggestion, placedPoint);

  // always use the first instance of this point
  const startIndex = nearestToStartPointResult.properties.index;
  const newStartIndex = findTopLinePointIndex(vertices, startIndex);

  const endIndex = nearestToPlacedPointResult.properties.index;

  // If coming from a non overlapping point and moving to an overlapping point,
  // use closest of 2 overlapping points, not necessarily the top line point
  const newEndIndex = pointHasManyMatches(vertices, startIndex)
    ? findTopLinePointIndex(vertices, endIndex)
    : findClosestPointIndex(vertices, newStartIndex, endIndex);

  // if the start point is already on the suggested route, use that point
  const firstPoint = isStartPointOnLine ? fromPoint : (nearestToStartPointResult.geometry.coordinates as LngLat);

  // use nearest point on the suggested route to the placed point, this will likely not be one of the vertices, but could be
  const endPoint = nearestToPlacedPointResult.geometry.coordinates as LngLat;

  // set first and last point of the result segment
  const result: LngLat[] = [firstPoint, endPoint];

  // no need for routing if its just point to point within one edge
  if (newStartIndex === newEndIndex) {
    return result;
  }

  // get all points in between
  // check both directions and use the shortest path
  const forwardPath = getPath(vertices, newStartIndex, newEndIndex);
  const reversed = vertices.toReversed();
  const backwardsPath = getPath(reversed, oppositeIndex(newStartIndex, reversed), oppositeIndex(newEndIndex, reversed) - 1);
  const forwardLine = lineString(forwardPath);
  const backwardsLine = lineString(backwardsPath);
  const forwardLength = turfLength(forwardLine);
  const backwardsLength = turfLength(backwardsLine);

  if (forwardLength < backwardsLength) {
    result.splice(1, 0, ...forwardPath);
  } else {
    result.splice(1, 0, ...backwardsPath);
  }

  if (!ignoreOrigStart && !isStartPointOnLine) {
    result.unshift(fromPoint);
  }

  if (!ignoreOrigEnd) {
    result.push(placedPoint);
  }
  // sometimes the route will have duplicate points, likely due to some faulty logic in this method or any that it calls
  // removeDuplicateVertices is a safeguard which ideally should not trigger
  return removeDuplicateVertices(result);
};

const createResult = (paths: LngLat[][], isSuccess: boolean): RouteAlongLineStringResult => {
  const data = {
    costings: { makeShorter: 0 },
    groupedSegments: [
      {
        pathGroups: [
          {
            paths: paths.map(path => ({
              points: path.map((point): LatLng => [point[1], point[0]])
            }))
          }
        ]
      }
    ]
  };
  return { data, isSuccess };
};

export const getPathsFromRouteLocations = (routes: ServerRoute[], lineStringObj: LineString): LngLat[][] => {
  const locations = routes[0].locations[0].map(location => [location.lng, location.lat]) as LngLat[];

  const pathsToRoute = locations.reduce(
    (paths: LngLat[][], pts, index) => (locations[index + 1] ? [...paths, [pts, locations[index + 1]]] : paths),
    []
  );

  const paths = pathsToRoute.map((pathStartAndEndPoints, index) => {
    const isLastOfManyPaths = pathsToRoute.length > 1 && index === pathsToRoute.length - 1;
    const isFirstPath = index === 0;

    return routeBetweenPoints(pathStartAndEndPoints, lineStringObj, { ignoreOrigStart: isLastOfManyPaths, ignoreOrigEnd: isFirstPath });
  });

  return paths;
};

export const routeAlongLineString = (routes: ServerRoute[], lineStringObj: LineString): RouteAlongLineStringResult => {
  try {
    const paths = getPathsFromRouteLocations(routes, lineStringObj);
    return createResult(paths, true);
  } catch (error) {
    console.error(error);
    return { data: undefined, isSuccess: false };
  }
};
